<template>
  <div>
    <!-- Banner Section -->
    <section :class="['confirmation-banner', bannerType]">
      <div
        v-if="!isError"
        class="confirmation-banner__content"
      >
        <div class="confirmation-banner__info">
          <div
            v-if="showCfpsId"
            class="confirmation-banner__code-wrapper"
          >
            <img
              src="https://cdn.ampersend.io/3IK5A9o1V88yXHn2DTJUsDTwkmI.svg"
              alt="Confirmed checkmark"
              class="confirmation-banner__check"
            >
            <div class="confirmation-banner__code-text">
              <h2 class="confirmation-banner__code-label">
                Your Confirmation Code:
              </h2>
              <strong
                ref="cfpsId"
                class="confirmation-banner__code-value"
              >{{ cfpsId | formatCfpsId }}</strong>
            </div>
          </div>
          <h5 class="confirmation-banner__code-description">
            Your USPS Change-of-Address has been submitted and is complete. No further action
            is required. You'll receive a <b>confirmation email to <span class="confirmation-banner__email">{{ email || 'the email provided' }}</span></b> shortly with more details.
          </h5>
          <div class="confirmation-banner__footer confirmation-banner__footer--success">
            Need to change another address? <a href="/mgo/disclaimer">Get Started Here</a>
          </div>
        </div>

        <ol class="confirmation-banner__columns">
          <li class="confirmation-banner__column">
            <p class="confirmation-banner__column-heading">
              Save This Confirmation
            </p>
            <p class="confirmation-banner__column-text">
              Please save this confirmation page for your records. Local agencies and/or
              resources may require it for proof of your address.
            </p>
            <a
              href="#"
              class="confirmation-banner__column-link"
              @click.prevent="saveConfirmation"
            >Save Confirmation</a>
          </li>

          <li class="confirmation-banner__column">
            <p class="confirmation-banner__column-heading">
              Keep an Eye on Your Mail
            </p>
            <p class="confirmation-banner__column-text">
              You should receive a mailed communication from the U.S. Postal Service
              at your <b>new address</b> within 7-10 Postal business days of your mail
              forwarding date.
            </p>
          </li>
        </ol>
      </div>

      <div
        v-else
        id="in-person-proofing"
        class="confirmation-banner__error-content"
      >
        <div class="confirmation-banner__error-left">
          <div class="confirmation-banner__error-header">
            <img
              src="https://cdn.ampersend.io/3IK5AYntx6YqUkVTsyAuB54SaMK.svg"
              alt="error Icon"
              class="confirmation-banner__error-icon"
            >
            <h3 class="confirmation-banner__error-title">
              Your Change-of-Address is NOT Complete
            </h3>
          </div>
          <p class="confirmation-banner__error-description">
            {{ errorMessage }}
          </p>
          <div class="confirmation-banner__footer">
            Need to change another address? <a href="/mgo/disclaimer">Get Started Here</a>
          </div>
        </div>

        <div class="confirmation-banner__error-right">
          <div class="confirmation-banner__next-steps">
            <h4 class="confirmation-banner__next-steps-title">
              What Happens Next
            </h4>
            <p class="confirmation-banner__next-steps-text">
              You will receive an email to <strong>{{ email || 'the email provided' }}</strong> or mailed letter to <strong>{{ oldAddress.streetAddress || 'your old address' }}</strong> with instructions to complete your request. Action must be taken by <strong>{{ actionDeadline }}</strong>.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Success Page Section -->
    <section class="confirmation-success">
      <div class="confirmation-success__wrapper">
        <div class="confirmation-success__left">
          <div class="powered-by-header">
            <img 
              src="https://cdn.ampersend.io/3IK5BRsDHEPWPkSRVLnyIPGHz69.svg"
              alt="Powered by MYMOVE" 
              class="mymove-logo-header"
            >
          </div>
          
          <h2 class="confirmation-success__heading">
            Next, Complete To-Dos for Your
            <br>
            {{ moverType === 'BUSINESS' ? 'New Address' : 'New Home' }} with MYMOVE
          </h2>
          
          <p class="confirmation-success__subheading">
            MYMOVE is proud to be an authorized affiliate of USPS, helping you {{ moverType === 'BUSINESS' ? 'get back to business faster' : 'get settled faster' }} with seamless service set up and local discounts.
          </p>

          <div class="offers-list">
            <div
              v-if="moverType !== 'BUSINESS' && successPageData.firstBox"
              class="offer-item"
            >
              <div class="offer-item__icon">
                <img
                  :src="tileSelection(successPageData.firstBox).icon"
                  :alt="tileSelection(successPageData.firstBox).heading"
                >
              </div>
              <div class="offer-item__content">
                <div class="offer-item__heading">
                  {{ tileSelection(successPageData.firstBox).heading }}
                </div>
                <div class="offer-item__subheading">
                  {{ tileSelection(successPageData.firstBox).subheading }}
                </div>
              </div>
            </div>

            <div
              v-if="successPageData.secondBox"
              class="offer-item"
            >
              <div class="offer-item__icon">
                <img
                  :src="tileSelection(successPageData.secondBox).icon"
                  :alt="tileSelection(successPageData.secondBox).heading"
                >
              </div>
              <div class="offer-item__content">
                <div class="offer-item__heading">
                  {{ tileSelection(successPageData.secondBox).heading }}
                </div>
                <div class="offer-item__subheading">
                  {{ tileSelection(successPageData.secondBox).subheading }}
                </div>
              </div>
            </div>

            <div
              v-if="successPageData.thirdBox"
              class="offer-item"
            >
              <div class="offer-item__icon">
                <img
                  :src="tileSelection(successPageData.thirdBox).icon"
                  :alt="tileSelection(successPageData.thirdBox).heading"
                >
              </div>
              <div class="offer-item__content">
                <div class="offer-item__heading">
                  {{ tileSelection(successPageData.thirdBox).heading }}
                </div>
                <div class="offer-item__subheading">
                  {{ tileSelection(successPageData.thirdBox).subheading }}
                </div>
              </div>
            </div>

            <div
              v-if="successPageData.fourthBox"
              class="offer-item"
            >
              <div class="offer-item__icon">
                <img
                  :src="tileSelection(successPageData.fourthBox).icon"
                  :alt="tileSelection(successPageData.fourthBox).heading"
                >
              </div>
              <div class="offer-item__content">
                <div class="offer-item__heading">
                  {{ tileSelection(successPageData.fourthBox).heading }}
                </div>
                <div class="offer-item__subheading">
                  {{ tileSelection(successPageData.fourthBox).subheading }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="confirmation-success__right">
          <div class="autofill-panel">
            <div
              class="autofill-panel__info"
              :class="{ 'autofill-panel__info--compact': isNewAddressFallback }"
            >
              <h3 class="autofill-panel__heading">
                Start by Autofilling Your Move Info
              </h3>
              <div
                v-if="!isNewAddressFallback"
                class="autofill-panel__info-content"
              >
                <div class="info-section">
                  <div class="info-section__label">
                    {{ moverType === 'BUSINESS' ? 'Business Details' : 'Personal Details' }}
                  </div>
                  <div class="info-section__content">
                    <template v-if="moverType === 'BUSINESS'">
                      <div>{{ businessName }}</div>
                    </template>
                    <template v-else>
                      <div>{{ firstName }} {{ lastName }}</div>
                    </template>
                    <div>{{ formatPhoneNumber(mobilePhone) }}</div>
                    <div>{{ email }}</div>
                  </div>
                </div>

                <div class="info-section">
                  <div class="info-section__label">
                    Move Date
                  </div>
                  <div class="info-section__content">
                    {{ userMoveDate }}
                  </div>
                </div>

                <div class="info-section">
                  <div class="info-section__label">
                    Old Address
                  </div>
                  <div class="info-section__content">
                    {{ formattedOldAddress }}
                  </div>
                </div>

                <div class="info-section">
                  <div class="info-section__label">
                    New Address
                  </div>
                  <div class="info-section__content">
                    {{ formattedNewAddress }}
                  </div>
                </div>
              </div>
              <div
                v-else
                class="info-checklist"
              >
                <div
                  v-for="(item, index) in infoItems"
                  :key="index"
                  class="info-checklist__item"
                >
                  <svg
                    v-if="item.isFallback"
                    class="info-item__check"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.5 4L6 11.5L2.5 8"
                      stroke="#004B87"
                      stroke-width="1.6"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <span>{{ item.value }}</span>
                </div>
              </div>
            </div>
            <div
              v-if="moverType === 'BUSINESS'"
              class="autofill-panel__consent"
            >
              <p class="autofill-panel__consent-text">
                By selecting “Yes, Autofill My Info”, I am voluntarily providing
                my consent to disclose a copy of my personal contact and change of 
                address information above to MYMOVE in order to autofill my MYMOVE 
                registration. I understand that I am leaving USPS.com and being redirected 
                to the MYMOVE website. I understand that I will be bound by MYMOVE’s Privacy 
                Policy and will no longer be subject to USPS’s Privacy Policy. I understand that,
                regardless of my consent to transfer my change of address and contact information, 
                I will be presented with advertisements for services such as internet, security, 
                insurance, and more. I understand that my data may be shared with those entities 
                for any purpose, including for non-move related purposes. My contact information 
                will not be retained by MYMOVE until I have had an opportunity to review and accept 
                MYMOVE's Privacy Policy and Terms and Conditions and have taken action to register with MYMOVE.
              </p>
            </div>
            <div
              v-else
              class="autofill-panel__consent"
            >
              <p class="autofill-panel__consent-text">
                By selecting “Yes, Autofill My Info”, I am voluntarily providing my consent to disclose a
                copy of my personal contact and change of address information above to MYMOVE in order to
                autofill my MYMOVE registration. I understand that I am leaving USPS.com and being redirected 
                to the MYMOVE website. I understand that I will be bound by MYMOVE’s Privacy Policy and will no 
                longer be subject to USPS’s Privacy Policy. I understand that, regardless of my consent to transfer 
                my change of address and contact information, I will be presented with advertisements for services 
                such as internet, security, insurance, and more. I understand that my data may be shared with those 
                entities for any purpose, including for non-move related purposes. My contact information will not 
                be retained by MYMOVE until I have had an opportunity to review and accept MYMOVE's Privacy Policy 
                and Terms and Conditions and have taken action to register with MYMOVE.
              </p>
            </div>
            <div class="autofill-panel__wrapper">
              <div class="autofill-panel__checkbox">
                <label class="autofill-checkbox-label">
                  <input
                    id="autofill-consent-checkbox"
                    v-model="autofillSelected"
                    type="checkbox"
                    @change="trackAutofill"
                  >
                  <span class="checkbox-text">Yes, Autofill My Info</span>
                </label>
              </div>

              <button
                class="autofill-panel__button"
                @click="next"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { format, parse, isValid, addDays } from 'date-fns';
import { track } from '../../analytics/events';
import { validateHandoffUrl } from '../../utils/commonUtil';
import { getStateName } from '../../utils/stateNames';

export default {
  name: 'DesktopConfirmationSuccess',
  filters: {
    formatCfpsId(value) {
      return (value || '')
        .split('')
        .reduce((acc, char, indx) => {
          if (indx > 0 && indx < 15 && indx % 4 === 3) {
            return `${acc}${char}-`;
          }
          return `${acc}${char}`;
        }, '');
    },
  },
  props: {
    moverType: {
      type: String,
      default: '',
    },
    forwardType: {
      type: String,
      default: '',
    },
    showCfpsId: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      autofillSelected: false,
      loading: false,
      offersSelected: [],
      successPageData: {
        firstBox: '1',
        secondBox: '2',
        thirdBox: '3',
        fourthBox: '4',
      },
      email: '',
      firstName: '',
      lastName: '',
      businessName: '',
      mobilePhone: '',
      oldAddress: {},
      newAddress: {},
      userMoveDate: '',
    };
  },
  computed: {
    autofill: () => JSON.parse(sessionStorage.getItem('autofill')),
    doubleJeopardy() {
      return [this.autofill, this.autofillSelected].includes(true);
    },
    passedIvsVerification() {
      return JSON.parse(sessionStorage.getItem('passedIvsVerification'));
    },
    ippRegistrationSuccess() {
      return JSON.parse(sessionStorage.getItem('ippRegistrationSuccess'));
    },
    isError() {
      return this.ippRegistrationSuccess === false || this.passedIvsVerification === false;
    },
    bannerType() {
      return this.isError ? 'confirmation-banner--error' : 'confirmation-banner--success';
    },
    errorMessage() {
      if (this.ippRegistrationSuccess === false && this.passedIvsVerification === false) {
        return 'Please visit your local Post Office and complete a hard copy Change-of-Address form.';
      }
      if (this.ippRegistrationSuccess === true && this.passedIvsVerification === false) {
        return 'We could not verify your identity. You will receive email or written instruction on next steps.';
      }
      return '';
    },
    cfpsId() {
      const { search } = window.location;
      const params = new URLSearchParams(search);
      return params.get('cfpsId') || '';
    },
    actionDeadline() {
      return format(addDays(new Date(), 20), 'MM/dd/yyyy');
    },
    isFirstNameFallback() {
      return this.firstName === 'Full Name';
    },
    isBusinessNameFallback() {
      return !this.businessName;
    },
    isMobilePhoneFallback() {
      return this.mobilePhone === 'Phone Number';
    },
    isEmailFallback() {
      return this.email === 'Email Address';
    },
    isMoveDateFallback() {
      return this.userMoveDate === 'Move Date';
    },
    isOldAddressFallback() {
      return !this.oldAddress.streetAddress;
    },
    isNewAddressFallback() {
      return !this.newAddress.streetAddress;
    },
    formattedOldAddress() {
      return this.formatAddress(this.oldAddress) || 'Old Address';
    },
    formattedNewAddress() {
      return this.formatAddress(this.newAddress) || 'New Address';
    },
    infoItems() {
      const nameItem = this.moverType === 'BUSINESS'
        ? { value: this.businessName || 'Business Name', isFallback: this.isBusinessNameFallback }
        : { value: `${this.firstName} ${this.lastName}`.trim(), isFallback: this.isFirstNameFallback };

      return [
        nameItem,
        { value: this.mobilePhone, isFallback: this.isMobilePhoneFallback },
        { value: this.email, isFallback: this.isEmailFallback },
        { value: this.userMoveDate, isFallback: this.isMoveDateFallback },
        { value: this.formattedOldAddress, isFallback: this.isOldAddressFallback },
        { value: this.formattedNewAddress, isFallback: this.isNewAddressFallback },
      ];
    },
  },
  mounted() {
    this.loadDataFromStore();
  },
  methods: {
    saveConfirmation() {
      window.print();
    },
    formatMoveDate(dateStr) {
      if (!dateStr) return '';
      const parsedDate = parse(dateStr, 'yyyy-MM-dd', new Date());
      return isValid(parsedDate) ? format(parsedDate, 'MMMM d, yyyy') : '';
    },
    formatAddress(address) {
      if (!address || !address.streetAddress) return '';
      const {
        streetAddress, city, state, zipCode,
      } = address;
      const cityStateZip = [city, state].filter(Boolean).join(', ');
      return [streetAddress, [cityStateZip, zipCode].filter(Boolean).join(' ')]
        .filter(Boolean)
        .join(', ');
    },
    formatPhoneNumber(phone) {
      if (!phone || phone === 'Phone Number') return phone;
      const digits = phone.replace(/\D/g, '');
      if (digits.length === 10) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      }
      return phone;
    },
    loadDataFromStore() {
      if (!this.$store || !this.$store.get) return;

      this.email = this.$store.get('instantSavings/email') || 'Email Address';
      this.firstName = this.$store.get('instantSavings/firstName') || 'Full Name';
      this.lastName = this.$store.get('instantSavings/lastName') || '';
      this.businessName = this.$store.get('instantSavings/businessName') || '';
      this.mobilePhone = this.$store.get('instantSavings/mobilePhone') || 'Phone Number';
      this.oldAddress = this.$store.get('instantSavings/oldAddress') || {};
      this.newAddress = this.$store.get('instantSavings/newAddress') || {};
      this.userMoveDate = this.formatMoveDate(sessionStorage.getItem('startDate')) || 'Move Date';
    },
    trackAutofill() {
      track('ElementClicked', {
        name: `autofill-toggled-${this.doubleJeopardy ? 'on' : 'off'}`,
      });
    },
    tileSelection(tileNumber) {
      const tiles = {
        1: {
          heading: 'Set Up Internet & TV',
          subheading: 'Transfer your current service or set up a new one.',
         icon: 'https://cdn.ampersend.io/3IK5CM7dwWdLjOGEgxO2SPFOouH.svg',
        },
         2: {
          heading: 'Claim Mover Offers',
          subheading: 'Find savings for local and top national brands.',
          icon: 'https://cdn.ampersend.io/3IK5BpjXd7D0jVqygDlHNHVpu36.svg',
        },
        3: {
          heading: 'Update Auto Insurance',
          subheading: `${this.newAddress.state ? getStateName(this.newAddress.state) : 'Your state'} may require an update within 30 days.`,
          icon: 'https://cdn.ampersend.io/3IK5BQ9gHUNnUoSFoaoZBbI1txF.svg',
        },
        4: {
          heading: this.moverType === 'BUSINESS' ? 'Protect Your New Address' : 'Protect Your Home',
          subheading: this.moverType === 'BUSINESS' ? 'Get a security system quote.' : 'Get a home security quote for renters and homeowners.',
          icon: 'https://cdn.ampersend.io/3IK5BR03xVxTTBJd89EXW3Fdmkp.svg',
        },
      };
      return tiles[tileNumber] || tiles[2];
    },
    async next() {
      if (this.loading) return;

      this.loading = true;

      track('ElementClicked', {
        name: 'confirmation-success-next-button',
        offersSelected: this.offersSelected,
        autofillSelected: this.autofillSelected,
      });

      sessionStorage.setItem('autofill', this.doubleJeopardy);
      sessionStorage.setItem('offersSelected', JSON.stringify(this.offersSelected));

      const { url } = await this.$coa.handoffGetURL('offers', this.doubleJeopardy, this.$route.query);

      const validUrl = validateHandoffUrl(url);
      if (!validUrl) {
        this.loading = false;
        return;
      }
      window.location.href = validUrl;
    },
  },
};
</script>

<style scoped>
/* Banner Styles */
.confirmation-banner {
  max-width: 1200px;
  margin: 0 auto 24px;
  padding: 24px 32px;
  background-color: #21874808;
  border-left: 5px solid #218748;
  border-radius: 8px;
}

.confirmation-banner--error {
  background-color: #F4E3DB;
  border-left: 5px solid #E71921;
}

.confirmation-banner--success {
  background-color: #21874808;
  border-left: 5px solid #218748;
}

.confirmation-banner__error-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: start;
}

.confirmation-banner__error-left {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 406px;
}

.confirmation-banner__error-right {
  display: flex;
  gap: 16px;
  height: 100%;
  width: 638px;
}

.confirmation-banner__error-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.confirmation-banner__error-icon {
  flex-shrink: 0;
}

.confirmation-banner__error-title {
  font-size: 22px;
  font-weight: 700;
  color: #E71921;
  margin: 0;
}

.confirmation-banner__error-description {
  font-size: 16px;
  line-height: 22px;
  color: #000000;
  margin: 0;
}

.confirmation-banner__next-steps {
  background-color: #FFFFFF;
  border-radius: 4px;
  padding: 16px 20px;
  align-content: center;
}

.confirmation-banner__next-steps-title {
  font-size: 14px;
  font-weight: 700;
  color: #000000;
  margin: 0 0 8px 0;
}

.confirmation-banner__next-steps-text {
  font-size: 14px;
  line-height: 20px;
  color: #000000;
  margin: 0;
}

.confirmation-banner__content {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 40px;
  align-items: start;
}

.confirmation-banner__info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 432px;
}

.confirmation-banner__code-wrapper {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
}

.confirmation-banner__code-description {
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0;
  color: #000000;
}

.confirmation-banner__check {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  margin-top: 2px;
}

.confirmation-banner__code-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.confirmation-banner__code-label {
  font-size: 22px;
  font-weight: 700;
  color: #000000;
  line-height: 28px;
}

.confirmation-banner__code-value {
  font-size: 22px;
  font-weight: 700;
  color: #000000;
  line-height: 28px;
}

.confirmation-banner__email {
  font-weight: 700;
}

.confirmation-banner__columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  background-color: #ECF3EC;
  padding: 20px;
  list-style: none;
  counter-reset: confirmation-banner-columns;
  margin: 0;
  width: 638px;
  height: 100%;
  border-radius: 8px;
  align-content: center;
}

.confirmation-banner__column {
  counter-increment: confirmation-banner-columns;
}

.confirmation-banner__column:first-child {
  border-right: 1px solid #D8D8D8;
  padding-right: 20px;
}

.confirmation-banner__column:last-child {
  padding-right: 1px;
}

.confirmation-banner__column-heading {
  font-size: 14px;
  font-weight: 700;
  color: #000000;
  margin-bottom: 8px;
}

.confirmation-banner__column-heading::before {
  content: counter(confirmation-banner-columns) ". ";
}

.confirmation-banner__column-text {
  font-size: 14px;
  line-height: 20px;
  color: #000000;
}

.confirmation-banner__column-link {
  display: inline-block;
  text-decoration: underline;
  margin-top: 8px;
  font-size: 14px;
  font-weight: 400;
  color: #0068BB;
}

.confirmation-banner__footer {
  text-align: left;
  font-size: 14px;
  color: #000000;
  margin-top: 8px;
}

.confirmation-banner--error .confirmation-banner__footer {
  margin-top: 0;
}

.confirmation-banner__footer a {
  color: #0068BB;
  text-decoration: underline;
}

.confirmation-banner__footer--success {
  text-align: left;
}

/* Success Page Styles */
.confirmation-success {
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px;
  border: 1px solid #D8D8D8;
  border-radius: 16px;
}

.confirmation-success__wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 63px;
  align-items: center;
}

.confirmation-success__left {
  width: 406px;
}

.powered-by-header {
  margin-bottom: 12px;
}

.mymove-logo-header {
  height: 24px;
  width: 148px;
}

.confirmation-success__heading {
  font-size: 22px;
  font-weight: 700;
  color: #000000;
  line-height: 28px;
  letter-spacing: 0;
  margin-bottom: 12px;
}

.confirmation-success__subheading {
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  color: #000000;
  padding-bottom: 20px;
}

.offers-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.offer-item {
  display: flex;
  align-items: center;
  padding-top: 8px;
  padding-bottom: 8px;
  border-radius: 12px;
}

.offer-item__icon {
  width: 40px;
  height: 40px;
  margin-right: 16px;
  flex-shrink: 0;
}

.offer-item__icon img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.offer-item__content {
  flex: 1;
}

.offer-item__heading {
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0;
  color: #000000;
}

.offer-item__subheading {
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  letter-spacing: 0;
  color: #000000;
}

.confirmation-success__right {
  display: flex;
  height: 100%;
  width: 638px;
}

.autofill-panel__info-content {
  display: flex;
  justify-content: space-between;
}

.autofill-panel {
  background: #E7F6F8;
  border-radius: 4px;
  padding: 32px;
}

.autofill-panel__heading {
  font-weight: 700;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0;
  color: #000000;
  grid-column: 1 / -1;
  margin-bottom: 16px;
}

.autofill-panel__info {
  display: grid;
  margin-bottom: 24px;
  background: white;
  padding: 20px;
  border-radius: 4px;
}

.autofill-panel__info--compact {
  max-height: 114px;
}

.autofill-panel__wrapper {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 24px;
}

.info-section {
  width: 122.5px;
  height: 67px;
  gap: 4px;
display: flex;
  flex-direction: column;

}

.info-section__label {
  font-weight: 700;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0%;
  color: #000000;
}

.info-section__content {
  font-weight: 400;
  font-size: 12px;
  line-height: 14px;
  letter-spacing: 0;
  vertical-align: middle;
  color: #000000;
}

.info-section__value {
  display: flex;
  align-items: center;
  gap: 6px;
}

.info-checklist {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(2, auto);
  grid-auto-flow: column;
  column-gap: 24px;
  row-gap: 12px;
}

.info-checklist__item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 400;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0;
  color: #000000;
}

.info-item__check {
  flex-shrink: 0;
}

.autofill-panel__consent {
  margin-bottom: 20px;
}

.autofill-panel__consent-text {
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0;
  color: #000000;
}

.autofill-panel__checkbox {
  margin-bottom: 0;
   height: 44px;
}

.autofill-checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 16px;
  border: 1px solid #0068BB;;
  border-radius: 4px;
  background: white;
  transition: all 0.2s ease;
  height: inherit;
}

.autofill-checkbox-label:hover {
  background-color: #f8f8ff;
}

.autofill-checkbox-label input[type="checkbox"] {
  width: 20px;
  height: 20px;
  margin-right: 12px;
  cursor: pointer;
  accent-color: #004b87;
}

.checkbox-text {
  font-size: 16px;
  font-weight: 600;
  color: #004b87;
}

.autofill-panel__button {
  width: 100%;
   height: 44px;
  background-color: #004b87;
  color: white;
  font-size: 18px;
  font-weight: 600;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.autofill-panel__button:hover {
  background-color: #ededed;
  color: #004b87;
}

.autofill-panel__button:active {
  background-color: #fff;
  color: #004b87;
}

@media (max-width: 968px) {
  .confirmation-success__wrapper {
    grid-template-columns: 1fr;
  }

  .confirmation-success__right {
    position: static;
  }

  .autofill-panel__info {
    grid-template-columns: 1fr;
  }
}
</style>